import { useMemo } from "react";
import Interaction from "../../../models/interactions/Interaction";
import { ISetVariable } from "../../../models/interactions/SetVariable";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import VariableSelector from "../variables/VariableSelector";
import PiInputfield from "../../../components/PiInputField";
import { useSelector } from "react-redux";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import { VariableType } from "../../../models/interactions/Variable";
import NodeToggle from "../components/NodeToggle";
import NodeNumberField from "../components/NodeNumberField";

export default function SetVariableNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const setVariableNode = props.interaction as ISetVariable;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const variables = useSelector(selectSequenceVariables);

  const selectedVariable = useMemo(() => {
    return variables.find((v) => v.id === setVariableNode.variable);
  }, [variables, setVariableNode.variable]);

  const renderValueInput = () => {
    if (!selectedVariable) {
      return (
        <div className="text-[12px] text-creator-text-sub">
          Select a variable first
        </div>
      );
    }

    switch (selectedVariable.type) {
      case VariableType.Boolean:
        return (
          <NodeToggle
            label="Value"
            value={setVariableNode.data as boolean}
            onChange={(checked) =>
              updateInteraction(props.nodeId, {
                ...setVariableNode,
                data: checked,
              } as ISetVariable)
            }
          />
        );
      case VariableType.Number:
        return (
          <NodeNumberField
            label="Value"
            value={setVariableNode.data as number}
            onChange={(num) =>
              updateInteraction(props.nodeId, {
                ...setVariableNode,
                data: num,
              } as ISetVariable)
            }
          />
        );
      case VariableType.String:
      case VariableType.BuiltIn:
        return (
          <PiInputfield
            label="Value"
            placeholder="Text"
            value={setVariableNode.data.toString()}
            onChange={(val) =>
              updateInteraction(props.nodeId, {
                ...setVariableNode,
                data: val,
              } as ISetVariable)
            }
          />
        );
    }
  };

  return (
    <div className="w-full h-full flex flex-col gap-[12px]">
      <VariableSelector
        variables={variables}
        selectedVariableId={setVariableNode.variable}
        label="Variable"
        includeBuiltIns={false}
        onSelect={(variableId) => {
          // Find the new variable to get its default value
          const newVariable = variables.find((v) => v.id === variableId);
          // Reset data to the new variable's default value, or appropriate default based on type
          let defaultValue: boolean | string | number = "";
          if (newVariable) {
            defaultValue = newVariable.value;
          }
          updateInteraction(props.nodeId, {
            ...setVariableNode,
            variable: variableId,
            data: defaultValue,
          } as ISetVariable);
        }}
      />
      {renderValueInput()}
    </div>
  );
}
